import { Redirect, Switch, useLocation } from 'react-router-dom';

import { useFeature } from 'FeatureProvider';
import { useUser } from 'containers/Users';

import Products from 'containers/MenuMakerProducts';
import MenuMaker from 'containers/MenuMaker';
import Categories from 'containers/MenuMakerCategories';
import ModifierGroups from 'containers/MenuMakerModifierGroups';
import MenuMakerResume from 'containers/MenuMakerResume';
import PublishMenuMaker from 'containers/MenuMakerPublish';
import MenuMakerPreview from 'containers/MenuMakerPreview';
import MenuScope from 'containers/MenuScope';

import useUserStore from 'hooks/useUserStore';
import { useMenuScopeManagement } from 'hooks/menuScope';
import { MENU_ROUTES } from 'utils/menuMaker/constants';

import { useMemo } from 'react';

import AppRoute from '../PrivateRoute';

/**
 * Menu Maker Routes Component
 * Manages routing for all menu maker related pages with scope-based access control
 *
 * Features:
 * - Scope-based routing with automatic redirects
 * - Permission-based access control
 * - Legacy route support with redirects
 * - Draft mode handling for publish routes
 * - Feature flag based scope selection (mainMenuOrganization)
 *
 * @returns {JSX.Element} Switch component with all menu maker routes
 */
export function MenuMakerRoutes() {
  const location = useLocation();
  const {
    storeState: { selected: storeSelected, storeDraft },
  } = useUserStore();
  const { selectedScope } = useMenuScopeManagement();
  const { data: user } = useUser();

  // Separate permissions for module access vs organization access
  const superUserAccess = useFeature('adminStore');
  const menuMakerModuleAccess = useFeature('backofficeModuleMenu');

  // Permission to access the menu maker module (store OR organization level)
  const permissionMenuMakerModule = superUserAccess || menuMakerModuleAccess;

  // Permission to access organization level features (requires valid storeDraft)
  const permissionOrganizationAccess = !!storeDraft?.uuid;

  // Extract the specific flag we need to prevent useMemo from being recalculated on every user object change
  const hasMenuOrganizationFlag = user?.mainMenuOrganization === true;

  const ScopeComponent = useMemo(() => {
    return function WrappedMenuScope(props) {
      // If user already has a scope selected, redirect immediately to resume
      // This prevents the visual "jump" from scope selection to resume
      if (selectedScope) {
        return <Redirect to={MENU_ROUTES.RESUME} />;
      }

      // FEATURE FLAG: Only show scope selection if mainMenuOrganization is explicitly true
      // This allows gradual rollout of the new scope selection feature
      if (hasMenuOrganizationFlag) {
        // Show scope selection for users with the feature flag enabled
        return <MenuScope {...props} permissionOrganizationAccess={permissionOrganizationAccess} />;
      }

      // For all other cases (false, undefined), go directly to resume (legacy behavior)
      return <Redirect to={MENU_ROUTES.RESUME} />;
    };
  }, [permissionOrganizationAccess, selectedScope, hasMenuOrganizationFlag]);

  return (
    <Switch>
      <AppRoute accessRoute={permissionMenuMakerModule} component={ScopeComponent} exact path={MENU_ROUTES.ROOT} />
      {/* Only redirect to ROOT if no scope is selected AND user has the menu organization flag */}
      {!selectedScope && hasMenuOrganizationFlag && <Redirect to={MENU_ROUTES.ROOT} />}
      <AppRoute accessRoute={permissionMenuMakerModule} component={MenuMaker} exact path={MENU_ROUTES.MENUS} />
      <AppRoute accessRoute={permissionMenuMakerModule} component={Categories} exact path={MENU_ROUTES.CATEGORIES} />
      <AppRoute accessRoute={permissionMenuMakerModule} component={Products} exact path={MENU_ROUTES.PRODUCTS} />
      <AppRoute
        accessRoute={permissionMenuMakerModule}
        component={ModifierGroups}
        exact
        path={MENU_ROUTES.MODIFIER_GROUPS}
      />
      <AppRoute
        accessRoute={permissionMenuMakerModule && !storeSelected?.isDraft}
        component={PublishMenuMaker}
        exact
        path={MENU_ROUTES.PUBLISH}
      />
      <AppRoute accessRoute={permissionMenuMakerModule} component={MenuMakerPreview} exact path={MENU_ROUTES.PREVIEW} />
      <AppRoute accessRoute={permissionMenuMakerModule} component={MenuMakerResume} exact path={MENU_ROUTES.RESUME} />
      {/* Legacy routes - redirect to new routes */}
      <Redirect from="/menus" to={MENU_ROUTES.ROOT} />
      <Redirect from="/menus/categories" to={MENU_ROUTES.CATEGORIES} />
      <Redirect from="/menus/products" to={MENU_ROUTES.PRODUCTS} />
      <Redirect from="/menus/modifier-groups" to={MENU_ROUTES.MODIFIER_GROUPS} />
      <Redirect from="/menus/publish" to={MENU_ROUTES.PUBLISH} />
      <Redirect from="/menus/resume" to={MENU_ROUTES.RESUME} />
      <Redirect from="/menus/preview/:menuUuid" to={`${MENU_ROUTES.PREVIEW.split(':')[0]}:menuUuid`} />
    </Switch>
  );
}
